import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Video, ArrowLeft, Minus, Plus, Info, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VideoOrderPage = () => {
  const { eventId } = useParams();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: user?.phone || '',
    rider_team_name: '',
    horse_name: '',
    run_number: '',
    notes: '',
    num_videos: 1,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // Public single-event lookup
        try {
          const evRes = await axios.get(`${API_URL}/api/events/${eventId}/public`);
          if (evRes.data.event) {
            setEvent(evRes.data.event);
          } else {
            setEvent({ id: eventId, title: 'Event' });
          }
        } catch {
          setEvent({ id: eventId, title: 'Event' });
        }

        // Pricing
        const pRes = await axios.get(`${API_URL}/api/media-pricing`, { headers });
        setPricing(pRes.data);
      } catch (err) {
        console.error('Failed to load:', err);
      }
    };
    fetchData();
  }, [eventId, user]);

  const total = pricing
    ? (pricing.video_first_price + pricing.video_extra_price * (form.num_videos - 1)).toFixed(2)
    : '0.00';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !event) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API_URL}/api/video-orders`, {
        event_id: event.id,
        event_name: event.title || 'Event',
        ...form,
        num_videos: parseInt(form.num_videos),
        origin_url: window.location.origin,
      }, { headers });
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Order failed:', err);
      toast.error(err.response?.data?.detail || 'Order failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to={`/gallery/${eventId}`} className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4" data-testid="back-to-event">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Event
        </Link>

        <div className="text-center mb-8">
          <Video className="w-12 h-12 text-primary mx-auto mb-3" />
          <h1 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tight">Order Custom Video</h1>
          {event && <p className="text-lg text-muted-foreground mt-2">{event.title}</p>}
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-900">
            <p className="font-bold">Heads up — not every run is captured.</p>
            <p>We do our best, but we can't guarantee every single run was recorded. If we can't find your footage, we'll refund you.</p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-card border border-border rounded-2xl p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cust-name">Your Name *</Label>
              <Input
                id="cust-name"
                value={form.customer_name}
                onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))}
                required
                data-testid="vo-customer-name"
              />
            </div>
            <div>
              <Label htmlFor="cust-email">Email *</Label>
              <Input
                id="cust-email"
                type="email"
                value={form.customer_email}
                onChange={(e) => setForm(f => ({ ...f, customer_email: e.target.value }))}
                required
                data-testid="vo-customer-email"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="cust-phone">Phone (for delivery notification)</Label>
            <Input
              id="cust-phone"
              type="tel"
              value={form.customer_phone}
              onChange={(e) => setForm(f => ({ ...f, customer_phone: e.target.value }))}
              placeholder="+1 555 123 4567"
              data-testid="vo-customer-phone"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="rider">Rider / Team Name</Label>
              <Input
                id="rider"
                value={form.rider_team_name}
                onChange={(e) => setForm(f => ({ ...f, rider_team_name: e.target.value }))}
                placeholder="e.g. John Smith / Smith-Jones"
                data-testid="vo-rider-name"
              />
            </div>
            <div>
              <Label htmlFor="horse">Horse Name (optional)</Label>
              <Input
                id="horse"
                value={form.horse_name}
                onChange={(e) => setForm(f => ({ ...f, horse_name: e.target.value }))}
                data-testid="vo-horse-name"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="run">Run # / Draw # (if known)</Label>
            <Input
              id="run"
              value={form.run_number}
              onChange={(e) => setForm(f => ({ ...f, run_number: e.target.value }))}
              placeholder="e.g. Round 1, Draw #12"
              data-testid="vo-run-number"
            />
          </div>

          <div>
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Anything that helps us find your run — shirt color, time, partner name, etc."
              rows={3}
              data-testid="vo-notes"
            />
          </div>

          <div className="border-t border-border pt-5">
            <Label>Number of Videos</Label>
            <div className="flex items-center gap-3 mt-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setForm(f => ({ ...f, num_videos: Math.max(1, f.num_videos - 1) }))}
                data-testid="vo-decrease"
              >
                <Minus className="w-4 h-4" />
              </Button>
              <div className="font-display font-black text-3xl w-16 text-center" data-testid="vo-num-videos">
                {form.num_videos}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setForm(f => ({ ...f, num_videos: Math.min(50, f.num_videos + 1) }))}
                data-testid="vo-increase"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {pricing && (
            <div className="bg-muted/50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>First video</span>
                <span className="font-semibold">${pricing.video_first_price.toFixed(2)}</span>
              </div>
              {form.num_videos > 1 && (
                <div className="flex justify-between text-sm">
                  <span>{form.num_videos - 1} additional × ${pricing.video_extra_price.toFixed(2)}</span>
                  <span className="font-semibold">${(pricing.video_extra_price * (form.num_videos - 1)).toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-border pt-2 flex justify-between font-display font-bold text-xl">
                <span>Total</span>
                <span className="text-primary" data-testid="vo-total">${total} CAD</span>
              </div>
              {!pricing.is_subscriber && !pricing.is_admin_or_coach && (
                <div className="bg-primary/10 rounded-lg p-2 text-xs text-primary mt-2 flex items-start gap-2">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Subscribers pay ${pricing.pricing.video_first_sub.toFixed(2)} first + ${pricing.pricing.video_extra_sub.toFixed(2)} each. <Link to="/signup" className="font-bold underline">Save with a subscription →</Link>
                  </span>
                </div>
              )}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            disabled={submitting}
            className="w-full font-bold uppercase tracking-wider"
            data-testid="vo-submit"
          >
            {submitting ? 'Redirecting...' : `Pay $${total} & Submit Order`}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            We'll find your footage, edit it, and send you a download link. SMS delivery if you provided a phone number.
          </p>
        </form>
      </div>
    </div>
  );
};

export default VideoOrderPage;
